import { useMemo, useState, useEffect } from "react";
import api from "../../../api/Base";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

// Helpers
import { getTimeZone } from "../../../helpers/utils";
import Helper from "../../../helpers/Helper";

// Types
import { ActivityHashData, InstanceOptions } from "./types";
import ExecutionPlan from "../../../types/instance/ExecutionPlan";
import Wait from "../../../types/instance/Wait";

// Components
import StatementWaits from "./statement-waits";
import { CHART_COLOURS_WAITS } from "../../../component/Constants";

type StatementExecutionPlansProps = {
    waitTimes: number[];
    statementsData: ActivityHashData[],
    setExecutionPlans: Function,
    rowValues: any;
    instanceOptions: InstanceOptions[];
    interval: number;
};

const StatementExecutionPlans = ({
                                     waitTimes,
                                     rowValues,
                                     instanceOptions,
                                     setExecutionPlans,
                                     interval,
                                     statementsData,
                                 }: StatementExecutionPlansProps) => {
    const sqlHash = rowValues.sqlhash;
    const timezone = getTimeZone();

    const [executionPlansA, setExecutionPlansA] = useState<ExecutionPlan[]>([]);
    const [executionPlansB, setExecutionPlansB] = useState<ExecutionPlan[]>([]);
    const [waits, setWaits] = useState<Wait[][]>([])


    const matchedStatementsA = useMemo(
        () => statementsData.filter((item) => item.sqlhash === sqlHash),
        [statementsData, sqlHash]
    );

    const matchedStatementsB = useMemo(
        () => statementsData.filter((item) => item.sqlhash === sqlHash),
        [statementsData, sqlHash]
    );

    const fetchExecutionPlans = async(instance: InstanceOptions): Promise<ExecutionPlan[]> => {
        // if (!matchedStatementsA || matchedStatementsB) return [];

        const { periodStart, periodEnd } = instance;
        const from = dayjs(periodStart).format("YYYY-MM-DD+HH:mm");
        const to = dayjs(periodEnd).format("YYYY-MM-DD+HH:mm");

        try {
            const response = await api.get(
                `sql/plan?from=${from}&to=${to}&tz=${timezone}&interval=${interval}&sort=earliest&id=${instance.instanceId}&sqlhash=${sqlHash}`
            );
            return response.data.filter(
                (plan: ExecutionPlan) => plan.sqlplan !== null
            );
        } catch (error) {
            console.error("Failed to retrieve statement execution plans", error, matchedStatementsA, matchedStatementsB);
            return [];
        }
    };

    useEffect(() => {
        const getExecutionPlans = async() => {
            const promiseArray: Promise<ExecutionPlan[]>[] = instanceOptions.map((instanceOption: InstanceOptions) =>
                fetchExecutionPlans(instanceOption)
            );

            const results = await Promise.all(promiseArray);

            setExecutionPlansA(results[0]);
            setExecutionPlansB(results[1]);

            const updatedArrayA = results[0].map(item => ({
                ...item,
                event: "plan",
                timeslice: item.earliest
            }));
            const updatedArrayB =  results[1].map(item => ({
                ...item,
                event: "plan",
                timeslice: item.earliest
            }));
           setExecutionPlans([updatedArrayA, updatedArrayB])
        };


        void getExecutionPlans();
    }, [matchedStatementsA, matchedStatementsB, instanceOptions, interval, timezone, sqlHash]);

    useMemo(() => {
        async function getStatementWaits() {
            const newWaits: Wait[][] = [[], []]; // Initialize waits as an array of two arrays.

            // Fetch waits for matchedStatementsA.
            if (matchedStatementsA.length > 0) {
                const { periodStart, periodEnd } = instanceOptions[0];
                const from = dayjs(periodStart).format("YYYY-MM-DD+HH:mm");
                const to = dayjs(periodEnd).format("YYYY-MM-DD+HH:mm");

                try {
                    const response = await api.get<Wait[]>(
                        `activity/waitevent?from=${from}&to=${to}&tz=${timezone}&interval=${interval}&sort=waittime+desc&id=${instanceOptions[0].instanceId}&sqlhash=${sqlHash}`
                    );
                    newWaits[0] = response.data;
                } catch (error) {
                    console.error("Failed to retrieve top statement waits for A", error);
                }
            }

            // Fetch waits for matchedStatementsB.
            if (matchedStatementsB.length > 0) {
                const { periodStart, periodEnd } = instanceOptions[1];
                const from = dayjs(periodStart).format("YYYY-MM-DD+HH:mm");
                const to = dayjs(periodEnd).format("YYYY-MM-DD+HH:mm");

                try {
                    const response = await api.get<Wait[]>(
                        `activity/waitevent?from=${from}&to=${to}&tz=${timezone}&interval=${interval}&sort=waittime+desc&id=${instanceOptions[1].instanceId}&sqlhash=${sqlHash}`
                    );
                    newWaits[1] = response.data;
                } catch (error) {
                    console.error("Failed to retrieve top statement waits for B", error);
                }
            }

            // Remove duplicates within each set and assign colors.
            newWaits.forEach((waitList, index) => {
                newWaits[index] = waitList
                    .filter((wait, i, self) => self.findIndex(item => item.waitevent === wait.waitevent) === i)
                    .map((wait, i) => ({ ...wait, color: CHART_COLOURS_WAITS[i] }));
            });

            setWaits(newWaits); // Update state with two separate arrays.
        }

        void getStatementWaits();
    }, [matchedStatementsA, matchedStatementsB]);

    const renderRow = (matchedStatement: ActivityHashData, index: number) => {

        const waitTimeExist = index === 0 ? matchedStatement.waittime : matchedStatement.waittime_1

        const waitTimeValue = waitTimeExist ? Number(waitTimeExist) : 0
        const waitTime = Helper.getTimeInEnglish(waitTimeValue);
        let weightPercentage;

        const isValid = waitTimes[index] > 0 && matchedStatement;

        if (isValid) {
            const percentage = (100 * waitTimeValue) / waitTimes[index];
            const formattedPercentage = percentage.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            weightPercentage = `${formattedPercentage}%`;
        } else {
            weightPercentage = "-";
        }

        const { periodStart, periodEnd } = instanceOptions[index];
        const from = dayjs(periodStart).format("YYYY-MM-DD+HH:mm");
        const to = dayjs(periodEnd).format("YYYY-MM-DD+HH:mm");
        const executionPlansLength = index === 0 ? executionPlansA.length : executionPlansB.length

        return (
            <tr>
                <td>
                    <Link className='fw-bold'
                          to={`/instances/${instanceOptions[index].instanceId}/activity/statement/${sqlHash}/text?fm=${from}&to=${to}&tz=${timezone}&interval=${interval}`}
                          target="_blank">{Helper.getFormattedPeriodHeadingFromDate(instanceOptions[index]?.periodStart, instanceOptions[index]?.periodEnd)}</Link>
                    <sup className="fal fa-external-link fa-fw"></sup>
                </td>
                <td className='text-end'>
                    {executionPlansLength || '-'}
                </td>
                <td className='text-end'>
                    {waitTime && waits[index] ? <div className='d-flex justify-content-end'><StatementWaits
                        statement={matchedStatement} waits={waits[index]} instanceTime={waitTimes[index]}
                        instance={instanceOptions[index]}/></div> : '-'}
                </td>
                <td className='text-end'>{waitTime || '-'}</td>
                <td className='text-end'>{weightPercentage}</td>
            </tr>
        );
    };

    const getDifferenceText = (values: any, formatFn: Function) => {
        if (values[0] && values[1]) {
            const percent = Helper.getFormattedNumber(Helper.getPercentageDifference(values[1], values[0]));
            const diff = formatFn(Math.abs(values[0] - values[1]));
            return `${diff} (${percent}%)`;
        }
        return '';
    };

    const executionsPlansDifference = useMemo(
        () => {
            const executionsA = executionPlansA.length
            const executionsB = executionPlansB.length


            if (!executionsA || !executionsB) {
                return ''
            }
            return getDifferenceText([executionsA, executionsB], Helper.getFormattedNumber)
        }, [executionPlansA, executionPlansB]
    );

    const timeDifference = useMemo(
        () => {
            const waittimeA = matchedStatementsA[0]?.waittime
            const waittimeB = matchedStatementsB[0]?.waittime_1
            if (!waittimeA || !waittimeB) {
                return ''
            }
            return getDifferenceText([waittimeA, waittimeB], Helper.getTimeInEnglish)
        }, [matchedStatementsA, matchedStatementsB]
    );

    const calculateWeightPercentage = (waitTimeValue: number, waitTime: number) => {
        if (waitTime > 0) {
            const percentage = (100 * waitTimeValue) / waitTime;
            return `${percentage.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}%`;
        }
        return "-";
    };

    const waitTimeValue1 = Number(matchedStatementsA[0]?.waittime);
    const waitTimeValue2 = Number(matchedStatementsB[0]?.waittime_1);

    const weightPercentage1 = waitTimes.length && matchedStatementsA[0]
            ? calculateWeightPercentage(waitTimeValue1, waitTimes[0])
            : "-";

    const weightPercentage2 = waitTimes.length && matchedStatementsB[0]
            ? calculateWeightPercentage(waitTimeValue2, waitTimes[1])
            : "-";

    const percentageDifference = useMemo(() => {

        if (weightPercentage1 === "-" || weightPercentage2 === "-") return "";

        const diff = parseInt(weightPercentage2.replace(/,/g, ""), 10) - parseInt(weightPercentage1.replace(/,/g, ""), 10);
        return Math.abs(diff) ? `${Math.abs(diff)}%` : '';
    }, [matchedStatementsA, matchedStatementsB]);

    return (
        <div className="row row-cols-lg-12 mt-3">
            <table className="table">
                <thead>
                <tr>
                    <th className="col-2" scope="col">
                        Period
                    </th>
                    <th className="col-2 text-end" scope="col">
                        <sup>
                            <i className="fal fa-ruler-triangle fa-fw mr-1"></i>
                        </sup>
                        Execution Plans
                    </th>
                    <th className="col-2 text-end" scope="col">
                        Top Statement Waits
                    </th>
                    <th className="col-2 text-end" scope="col">
                        Wait Time
                    </th>
                    <th className="col-2 text-end" scope="col">
                        Weight Percentage
                    </th>
                </tr>
                </thead>
                <tbody>
                {renderRow(matchedStatementsA[0], 0)}
                {renderRow(matchedStatementsB[0], 1)}
                </tbody>
                <tfoot>
                <tr>
                    <td>
                        Difference
                    </td>
                    <td className='text-end'>

                        {executionsPlansDifference || '-'}
                        {executionsPlansDifference &&
                            <i className={Helper.getChangeIcon(executionPlansA.length, executionPlansB.length)}/>}
                    </td>
                    <td className='text-end'></td>
                    <td className='text-end'>
                        {timeDifference || '-'}
                        {/*@ts-ignore*/}
                        {timeDifference && <i className={Helper.getChangeIcon(waitTimeValue1, waitTimeValue2)}/>}
                    </td>
                    <td className='text-end'>
                        {percentageDifference || '-'}
                        {/*@ts-ignore*/}
                        {percentageDifference ? <i className={Helper.getChangeIcon(Number(weightPercentage1.replace('%', '')), Number(weightPercentage2.replace('%', '')))}/> : ''}
                    </td>
                </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default StatementExecutionPlans;
